// La conversación de un contratado, como Telegram (S10): burbujas con cola, agrupadas por remitente, hora
// y ✓✓ dentro de la burbuja, separadores de día, los avisos como píldoras de servicio centradas y el
// trabajo del agente como una línea de servicio discreta. Los datos salen de lineaDeTiempo + agrupar
// (puras, probadas); aquí solo se pinta. Scroll, paginación y streaming son los del motor de chat de /home.
import { ArrowDown, ArrowRight, Check, CheckCheck, ChevronRight, Clock, FileText, Languages, Loader2, Mic, TriangleAlert } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../stores/app.store';
import { getActiveBaseUrl } from '../../../stores/servers.store';
import { useChatScroll } from '../../../hooks/useChatScroll';
import { usePaginatedHistory } from '../../../hooks/usePaginatedHistory';
import { useInfiniteScrollUp } from '../../../hooks/useInfiniteScrollUp';
import { MarkdownRenderer } from '../../../components/MarkdownRenderer';
import { formatClockTime, formatDiaMes } from '../../../lib/time';
import type { Message, StoredAttachment } from '../../../types';
import type { ContratadoDTO, Fase } from '../compartido/vocabulario';
import { IconoEncargo } from '../ui';
import { esInforme, portada } from '../informe/bloques';
import { Cola } from './Cola';
import { MiniaturaImagen } from './VisorImagen';
import { useVisorDocumento } from './VisorDocumento';
import { agrupar, diaLocal } from './agrupar';
import { lineaDeTiempo, type Elemento, type Entrega, type Hito } from './linea-de-tiempo';

const AVATAR = 34;
// El texto del agente viene en Markdown; sus enlaces y código, con la paleta del chat.
const MARKDOWN = '[&_a]:!text-oficina-enlace [&_a]:!no-underline hover:[&_a]:!underline [&_code]:!bg-black/25 [&_code]:!text-oficina-texto [&_pre]:!bg-black/30 [&_pre]:!rounded-lg';

/**
 * `traducciones`: mensajes del agente que salieron en inglés → su traducción; `resumenes`: mensajes largos → «en corto».
 * Los dos los escribe el resumidor de la ficha.
 * `pie` (v7, 01-11): lo que va al final de la conversación y se desplaza con ella — desde la entrega 2 (04-11), la línea
 * «Te he preparado un pedido para Santi · Revisar» (pelotas/PedidoEnElChat). Cuenta como un mensaje más para el
 * desplazamiento al final.
 */
export function ConversacionOficina({ contratado, mensajes, traducciones = {}, resumenes = {}, pie }: { contratado: ContratadoDTO; mensajes: Message[]; traducciones?: Record<string, string>; resumenes?: Record<string, string>; pie?: ReactNode }) {
  const agentId = contratado.agentId;
  const parcial = useAppStore((s) => s.partialTextByAgent[agentId]);
  const trabajando = useAppStore((s) => s.agents[agentId]?.status === 'running');
  const filas = useMemo(() => agrupar(lineaDeTiempo(mensajes, { trabajando })), [mensajes, trabajando]);
  const { containerRef, newCount, scrollToBottom, isUserScrolledUp } = useChatScroll({
    agentId,
    messageCount: filas.length + (parcial !== undefined ? 1 : 0) + (pie ? 1 : 0),
    partialActive: parcial !== undefined,
    tailId: mensajes[mensajes.length - 1]?.id ?? null,
  });
  const { hasMore, loadingOlder, loadOlder } = usePaginatedHistory(agentId);
  useInfiniteScrollUp({ scrollContainerRef: containerRef, hasMore, loadingOlder, loadOlder, headId: mensajes[0]?.id ?? null });

  return (
    <div className="relative min-h-0 flex-1 bg-oficina-elevada">
      <div ref={containerRef} className="absolute inset-0 overflow-y-auto overscroll-contain" data-testid="conversacion-oficina">
        {/* Todo el ancho: el agente a la izquierda y tú a la derecha, como Telegram (Pau 28-09: «todo apilado en el centro»). */}
        <ol className="flex min-h-full w-full flex-col justify-end px-4 pb-4 pt-3 sm:px-8">
          {hasMore && loadingOlder ? (
            <li className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-oficina-texto3" aria-label={t('oficinaV2.chat.cargandoAnteriores')} />
            </li>
          ) : null}
          {filas.map((f) =>
            f.tipo === 'dia' ? (
              <li key={f.clave} className="flex justify-center py-2">
                <Servicio>{fechaDia(f.fecha)}</Servicio>
              </li>
            ) : f.tipo === 'servicio' ? (
              <li key={f.clave} data-message-id={f.elementos[0]!.id} data-message-ids={f.elementos.flatMap((e) => e.ids).join(' ')} className="my-2 flex justify-center rounded-o-m transition-shadow">
                <LineaServicio elementos={f.elementos} />
              </li>
            ) : (
              <li
                key={f.clave}
                data-message-id={f.e.id}
                data-message-ids={f.e.ids.join(' ')}
                className={cn('rounded-o-m transition-shadow', f.e.tipo === 'pau' || f.e.tipo === 'agente' ? (f.primero ? 'mt-2' : 'mt-0.5') : 'my-2')}
              >
                <Pieza e={f.e} ultimo={f.ultimo} contratado={contratado} traducciones={traducciones} resumenes={resumenes} />
              </li>
            ),
          )}
          {/* 13-10 — lo que va escribiendo mientras trabaja no se pinta en directo (es el parte que se pliega):
              solo los puntos de «trabajando»; lo que sea para Pau aparece al terminar. */}
          {trabajando ? (
            <li className="mt-2" aria-live="polite">
              <BurbujaAgente contratado={contratado} ultimo>
                <span className="flex items-center gap-1 py-1" aria-label={t('oficinaV2.chat.escribiendo')}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="h-2 w-2 animate-bounce rounded-full bg-oficina-texto3" style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </span>
              </BurbujaAgente>
            </li>
          ) : null}
          {pie ? <li data-testid="pie-conversacion">{pie}</li> : null}
        </ol>
      </div>
      {isUserScrolledUp || newCount > 0 ? (
        <button
          type="button"
          onClick={() => scrollToBottom(true)}
          aria-label={t('oficinaV2.chat.alFinal')}
          className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-oficina-control text-oficina-texto2 shadow-o-flota transition-colors hover:text-oficina-texto"
        >
          <ArrowDown className="h-5 w-5" />
          {newCount > 0 ? (
            <span className="absolute -top-1.5 left-1/2 flex h-5 min-w-5 -translate-x-1/2 items-center justify-center rounded-full bg-oficina-acento px-1.5 text-o-nota font-semibold text-oficina-acento-tinta">
              {newCount}
            </span>
          ) : null}
        </button>
      ) : null}
    </div>
  );
}

/** 29-09: cuántos archivos no pudo abrir, no cuáles (el nombre de un fichero es jerga para Pau). */
export function textoNoSePudieron(n: number): string {
  return t(n === 1 ? 'oficinaV2.chat.noSePudoUno' : 'oficinaV2.chat.noSePudieronVarios', { n });
}

function fechaDia(ts: string): string {
  const hoy = diaLocal(new Date().toISOString());
  const ayer = diaLocal(new Date(Date.now() - 86_400_000).toISOString());
  const d = diaLocal(ts);
  if (d === hoy) return t('oficinaV2.chat.hoy');
  if (d === ayer) return t('oficinaV2.chat.ayer');
  return formatDiaMes(ts);
}

function Pieza({ e, ultimo, contratado, traducciones, resumenes }: { e: Elemento; ultimo: boolean; contratado: ContratadoDTO; traducciones: Record<string, string>; resumenes: Record<string, string> }) {
  switch (e.tipo) {
    case 'pau':
      return <BurbujaPau e={e} ultimo={ultimo} agentId={contratado.agentId} />;
    case 'pasos':
      return <LineaPasos e={e} />;
    case 'agente':
      return <BurbujaDelAgente e={e} ultimo={ultimo} contratado={contratado} traduccion={traducciones[e.id]} resumen={resumenes[e.id]} />;
    case 'trabajo':
      return <LineaServicio elementos={[e]} />;
    case 'hito':
      return <PildoraHito hito={e.hito} />;
  }
}

/** A partir de este largo, sin «en corto» todavía, el mensaje se enseña recortado con «Leer entero». */
const LARGO_RECORTE = 700;

export function BurbujaDelAgente({ e, ultimo, contratado, traduccion, resumen }: { e: Extract<Elemento, { tipo: 'agente' }>; ultimo: boolean; contratado: ContratadoDTO; traduccion?: string; resumen?: string }) {
  // Una traducción mala o un falso «inglés» no puede esconder lo que escribió: «Ver el original» lo enseña.
  const [original, setOriginal] = useState(false);
  // 13-10 (Pau: «me da pena leerme estos textos cada vez»): un mensaje largo enseña su «en corto» y el resto, plegado.
  // 14-10 (Pau: «al darle a leer entero se cambia todo el mensaje y tengo que volver a leerlo; es el doble de trabajo»):
  // el «en corto» se queda arriba y el mensaje se abre debajo; sin «en corto», el principio recortado sigue en su sitio y
  // se abre el resto. Un «en corto» guardado a medias (antes del 14-10 acababa en «…») no se enseña: sale el mensaje.
  const [entero, setEntero] = useState(false);
  const texto = traduccion && !original ? traduccion : e.texto;
  const enCorto = resumen && !resumen.endsWith('…') ? resumen : undefined;
  const largo = texto.length >= LARGO_RECORTE;
  return (
    <BurbujaAgente contratado={contratado} ultimo={ultimo} hora={e.ts}>
      {e.adjuntos?.length ? <AdjuntosDelAgente adjuntos={e.adjuntos} agentId={contratado.agentId} /> : null}
      {enCorto ? (
        <p data-testid="en-corto" className="text-o-cuerpo leading-[1.45]">
          <span className="font-semibold">{t('oficinaV2.chat.enCorto')} </span>
          {enCorto}
        </p>
      ) : null}
      {e.texto && (!enCorto || entero) ? (
        <div data-testid="mensaje-entero" className={cn(MARKDOWN, enCorto && 'mt-2 border-t border-oficina-borde pt-2', !enCorto && largo && !entero && 'line-clamp-6')}>
          <MarkdownRenderer text={texto} />
        </div>
      ) : null}
      {e.texto && (enCorto || largo) ? (
        <button
          type="button"
          onClick={() => setEntero(!entero)}
          aria-expanded={entero}
          className="mt-1 text-o-sub font-medium text-oficina-enlace hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-oficina-acento"
        >
          {t(enCorto ? (entero ? 'oficinaV2.chat.ocultarMensaje' : 'oficinaV2.chat.leerMensaje') : entero ? 'oficinaV2.chat.leerMenos' : 'oficinaV2.chat.leerEntero')}
        </button>
      ) : null}
      {traduccion ? (
        <p className="flex items-center gap-1 text-o-nota text-oficina-texto3">
          <Languages className="h-3.5 w-3.5" aria-hidden />
          {t('oficinaV2.chat.traducido')} ·
          <button type="button" onClick={() => setOriginal(!original)} className="text-oficina-enlace hover:underline">
            {t(original ? 'oficinaV2.chat.verTraduccion' : 'oficinaV2.chat.verOriginal')}
          </button>
        </p>
      ) : null}
      {e.telegram ? (
        <p className="text-o-sub text-oficina-texto3">{t(e.telegram === 'enviado' ? 'oficinaV2.chat.telegramEnviado' : 'oficinaV2.chat.telegramFallo')}</p>
      ) : null}
      {e.noSePudieron?.length ? (
        <p className="text-o-sub text-oficina-texto3">{textoNoSePudieron(e.noSePudieron.length)}</p>
      ) : null}
    </BurbujaAgente>
  );
}

// ── Burbujas ─────────────────────────────────────────────────────────────

function Hora({ ts, entrega, claro }: { ts: string; entrega?: Entrega; claro?: boolean }) {
  return (
    <span className={cn('pointer-events-none absolute bottom-1 right-2.5 flex items-center gap-0.5 text-o-nota leading-none', claro ? 'text-oficina-meta-pau' : 'text-oficina-texto3')}>
      {formatClockTime(ts)}
      {entrega === 'recibido' ? <CheckCheck className="h-3.5 w-3.5" aria-label={t('oficinaV2.chat.entrega.recibido')} /> : null}
      {entrega === 'enviado' ? <Check className="h-3.5 w-3.5" aria-label={t('oficinaV2.chat.entrega.enviado')} /> : null}
      {entrega === 'pendiente' ? <Clock className="h-3 w-3" aria-label={t('oficinaV2.chat.entrega.pendiente')} /> : null}
      {entrega === 'no_entregado' ? <TriangleAlert className="h-3.5 w-3.5 text-oficina-bloqueado" aria-label={t('oficinaV2.chat.entrega.noEntregado')} /> : null}
    </span>
  );
}

// Reserva el hueco de la hora al final del texto (el truco de Telegram), para que no lo pise.
const HuecoHora = ({ ancho }: { ancho: number }) => <span className="inline-block h-3 align-bottom" style={{ width: ancho }} aria-hidden />;

const NOTA_DE_VOZ = /^🎤 Nota de voz de Pau \((\d+:\d{2})[^)]*\)(?::\s*«([\s\S]*)»)?/;

function BurbujaPau({ e, ultimo, agentId }: { e: Extract<Elemento, { tipo: 'pau' }>; ultimo: boolean; agentId: string }) {
  const voz = NOTA_DE_VOZ.exec(e.texto);
  const imagenes = e.adjuntos.filter((a) => a.route === 'image');
  const audios = e.adjuntos.filter((a) => a.mimeType.startsWith('audio/'));
  const otros = e.adjuntos.filter((a) => a.route !== 'image' && !a.mimeType.startsWith('audio/'));
  return (
    <div className="flex justify-end pl-[20%]">
      <div
        className={cn(
          'relative max-w-[min(680px,100%)] rounded-o-l bg-oficina-burbuja-pau px-3.5 py-2 text-o-cuerpo text-oficina-texto',
          ultimo && 'rounded-br-o-s',
        )}
      >
        {imagenes.length ? (
          <div className={cn('-mx-2 mb-1.5 mt-0.5 grid gap-1 overflow-hidden rounded-o-l', imagenes.length > 1 && 'grid-cols-2')}>
            {imagenes.map((a) => (
              <MiniaturaImagen key={a.id} src={urlAdjunto(agentId, a)} nombre={a.displayName} />
            ))}
          </div>
        ) : null}
        {audios.map((a) => (
          <div key={a.id} className="mb-1 mt-1 flex items-center gap-2">
            <Mic className="h-4 w-4 shrink-0 text-oficina-meta-pau" aria-hidden />
            <audio controls preload="metadata" src={urlAdjunto(agentId, a)} className="h-9 w-64 max-w-full [color-scheme:dark]" />
          </div>
        ))}
        {otros.map((a) => (
          <Fichero key={a.id} a={a} agentId={agentId} />
        ))}
        {voz ? (
          voz[2] ? (
            <p className="whitespace-pre-wrap break-words italic text-white/90">
              {voz[2]}
              <HuecoHora ancho={e.entrega ? 64 : 44} />
            </p>
          ) : (
            <p className="text-white/80">
              {t('oficinaV2.chat.vozSinTexto', { duracion: voz[1] ?? '' })}
              <HuecoHora ancho={e.entrega ? 64 : 44} />
            </p>
          )
        ) : e.texto ? (
          <p className="whitespace-pre-wrap break-words">
            {e.texto}
            <HuecoHora ancho={e.entrega ? 64 : 44} />
          </p>
        ) : (
          <HuecoHora ancho={e.entrega ? 64 : 44} />
        )}
        <Hora ts={e.ts} entrega={e.entrega} claro />
        {ultimo ? <Cola lado="derecha" de="pau" className="text-oficina-burbuja-pau" /> : null}
      </div>
    </div>
  );
}

/** Lo que el agente le manda a Pau (imágenes en rejilla, como Telegram; documentos como ficha). */
function AdjuntosDelAgente({ adjuntos, agentId }: { adjuntos: StoredAttachment[]; agentId: string }) {
  const imagenes = adjuntos.filter((a) => a.route === 'image');
  const otros = adjuntos.filter((a) => a.route !== 'image');
  return (
    <>
      {imagenes.length ? (
        <div className={cn('-mx-2 mb-1.5 mt-0.5 grid gap-1 overflow-hidden rounded-o-l', imagenes.length > 1 && 'grid-cols-2')}>
          {imagenes.map((a) => (
            <MiniaturaImagen key={a.id} src={urlAdjunto(agentId, a)} nombre={a.displayName} />
          ))}
        </div>
      ) : null}
      {otros.map((a) => (
        <Fichero key={a.id} a={a} agentId={agentId} />
      ))}
    </>
  );
}

function BurbujaAgente({ contratado, ultimo, hora, children }: { contratado: ContratadoDTO; ultimo: boolean; hora?: string; children: ReactNode }) {
  return (
    <div className="flex items-end gap-2 pr-12">
      <div className="shrink-0" style={{ width: AVATAR }}>
        {ultimo ? <IconoEncargo icono={contratado.icono} semilla={contratado.semillaAvatar} tamano={AVATAR} /> : null}
      </div>
      <div
        data-globo="agente"
        className={cn(
          'relative min-w-0 max-w-[min(760px,78%)] rounded-o-l bg-oficina-burbuja-agente px-3.5 py-2 text-o-cuerpo text-oficina-texto',
          ultimo && 'rounded-bl-o-s',
        )}
      >
        {children}
        {hora ? (
          <>
            <HuecoHora ancho={40} />
            <Hora ts={hora} />
          </>
        ) : null}
        {ultimo ? <Cola lado="izquierda" de="agente" className="text-oficina-burbuja-agente" /> : null}
      </div>
    </div>
  );
}

function urlAdjunto(agentId: string, a: StoredAttachment): string {
  return `${getActiveBaseUrl()}/api/agents/${encodeURIComponent(agentId)}/attachments/${encodeURIComponent(a.id)}/raw`;
}

function tamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Un documento del chat: se abre en el visor de la oficina (03-11: nunca en otra pestaña). Un informe, como tarjeta. */
function Fichero({ a, agentId }: { a: StoredAttachment; agentId: string }) {
  const abrir = useVisorDocumento((s) => s.abrir);
  const alAbrir = () => abrir({ agentId, id: a.id, nombre: a.displayName, tipo: a.mimeType });
  if (esInforme(a)) {
    const { titulo, entradilla } = portada(a.textPreview ?? '');
    return (
      <button
        type="button"
        onClick={alAbrir}
        data-testid="tarjeta-informe"
        className="group my-1.5 block w-[min(420px,100%)] rounded-o-l border border-oficina-acento/40 bg-oficina-control/60 px-4 py-3 text-left outline-none transition-colors hover:border-oficina-acento focus-visible:ring-2 focus-visible:ring-oficina-acento"
      >
        <span className="block text-o-mini font-bold text-oficina-acento">{t('oficinaV2.visorDocumento.etiquetaInforme')}</span>
        <span className="mt-0.5 block text-o-cuerpo font-semibold leading-snug text-oficina-texto">{titulo ?? a.displayName.replace(/\.md$/i, '')}</span>
        {entradilla ? <span className="mt-1 line-clamp-2 block text-o-texto leading-[1.45] text-oficina-texto2">{entradilla}</span> : null}
        <span className="mt-2 inline-flex items-center gap-1 text-o-sub font-medium text-oficina-enlace">
          {t('oficinaV2.visorDocumento.leer')}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </span>
      </button>
    );
  }
  return (
    <button type="button" onClick={alAbrir} aria-label={t('oficinaV2.visorDocumento.abrir', { nombre: a.displayName })} className="my-1 flex w-full items-center gap-3 rounded-o-m py-1 pr-2 text-left outline-none hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
        <FileText className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-o-texto font-medium">{a.displayName}</span>
        <span className="block text-o-nota text-oficina-meta-pau">{tamano(a.size)}</span>
      </span>
    </button>
  );
}

// ── Lo que fue haciendo (13-10) ──────────────────────────────────────────

/**
 * Los textos que el agente escribió mientras trabajaba en un turno, plegados en una línea discreta a la altura de
 * sus burbujas. Se abre al tocarla (Pau: «quiero la respuesta, no el parte en directo»; el detalle sigue ahí).
 */
function LineaPasos({ e }: { e: Extract<Elemento, { tipo: 'pasos' }> }) {
  const [abierto, setAbierto] = useState(false);
  const n = e.textos.length;
  const cuantos = t(n === 1 ? 'oficinaV2.chat.pasos.uno' : 'oficinaV2.chat.pasos.varios', { n });
  const horas = e.hasta && formatClockTime(e.hasta) !== formatClockTime(e.ts) ? `${formatClockTime(e.ts)} – ${formatClockTime(e.hasta)}` : formatClockTime(e.ts);
  return (
    <div style={{ paddingLeft: AVATAR + 8 }} className="pr-12">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-label={t(abierto ? 'oficinaV2.chat.pasos.cerrar' : 'oficinaV2.chat.pasos.abrir')}
        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-dashed border-oficina-linea px-3 py-1 text-o-sub text-oficina-texto3 transition-colors hover:border-oficina-texto3 hover:text-oficina-texto2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-oficina-acento"
      >
        {e.enCurso ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 transition-transform', abierto && 'rotate-90')} aria-hidden />}
        <span className="font-medium text-oficina-texto2">{e.enCurso ? `${t('oficinaV2.chat.pasos.trabajando')} · ${cuantos}` : cuantos}</span>
        <span className="shrink-0">· {horas}</span>
      </button>
      {abierto ? (
        <ol className="mt-1.5 space-y-1.5 border-l border-oficina-linea pl-3">
          {e.textos.map((p) => (
            <li key={p.id} data-message-id={p.id} className="text-o-sub leading-[1.45] text-oficina-texto3">
              <span className="mr-1.5 text-oficina-texto3/70">{formatClockTime(p.ts)}</span>
              <span className="whitespace-pre-wrap break-words">{p.texto}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

// ── Mensajes de servicio ─────────────────────────────────────────────────

function Servicio({ children, tono = 'normal' }: { children: ReactNode; tono?: 'normal' | 'te_necesita' | 'error' }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-[90%] items-center gap-1.5 rounded-full px-3 py-1 text-center text-o-sub font-medium leading-snug',
        tono === 'te_necesita' ? 'bg-oficina-te-necesita/20 text-oficina-te-necesita-claro' : tono === 'error' ? 'bg-oficina-error/20 text-oficina-error-claro' : 'bg-black/35 text-white/85',
      )}
    >
      {children}
    </span>
  );
}

function textoTrabajo(e: Extract<Elemento, { tipo: 'trabajo' }>): string {
  const partes = [
    e.mirar ? t('oficinaV2.chat.trabajo.mirar', { n: e.mirar }) : null,
    e.cambiar ? t('oficinaV2.chat.trabajo.cambiar', { n: e.cambiar }) : null,
    e.comprobar ? t('oficinaV2.chat.trabajo.comprobar', { n: e.comprobar }) : null,
    e.ayudantes ? t('oficinaV2.chat.trabajo.ayudantes', { n: e.ayudantes }) : null,
    e.otros ? t('oficinaV2.chat.trabajo.otros', { n: e.otros }) : null,
    e.fallos ? t('oficinaV2.chat.trabajo.fallos', { n: e.fallos }) : null,
  ].filter((x): x is string => x !== null);
  return partes.join(', ');
}

/** Una sola línea gris con los avisos informativos seguidos (crítica A10): «Contratado · revisó cosas (4)». */
function LineaServicio({ elementos }: { elementos: Elemento[] }) {
  const enCurso = elementos.some((e) => e.tipo === 'trabajo' && e.enCurso);
  const textos = elementos.map((e) => (e.tipo === 'trabajo' ? textoTrabajo(e) : e.tipo === 'hito' ? textoHito(e.hito).texto : '')).filter(Boolean);
  const unido = textos.join(' · ');
  if (!unido && !enCurso) return null;
  return (
    <Servicio>
      {enCurso ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden /> : null}
      {unido.charAt(0).toUpperCase() + unido.slice(1)}
    </Servicio>
  );
}

function textoHito(h: Hito): { texto: string; tono: 'normal' | 'te_necesita' | 'error' } {
  switch (h.clase) {
    case 'contratado':
      return { texto: t('oficinaV2.chat.hito.contratado'), tono: 'normal' };
    case 'contrato_aprobado':
      return { texto: t('oficinaV2.chat.hito.contratoAprobado'), tono: 'normal' };
    case 'cambios_pedidos':
      return { texto: t('oficinaV2.chat.hito.cambiosPedidos', { texto: h.texto }), tono: 'normal' };
    case 'respondiste':
      return { texto: t('oficinaV2.chat.hito.respondiste', { texto: h.texto }), tono: 'normal' };
    case 'cierre_pedido':
      return { texto: t(h.quien === 'agente' ? 'oficinaV2.chat.hito.cierrePedidoAgente' : 'oficinaV2.chat.hito.cierrePedido'), tono: 'normal' };
    case 'integrar_pedido':
      return { texto: t('oficinaV2.chat.hito.integrarPedido'), tono: 'normal' };
    case 'integrado':
      return { texto: t('oficinaV2.chat.hito.integrado'), tono: 'normal' };
    case 'integrar_fallo':
      return { texto: t('oficinaV2.chat.hito.integrarFallo'), tono: 'error' };
    case 'aviso_oficina':
      return { texto: t('oficinaV2.chat.hito.avisoOficina'), tono: 'normal' };
    case 'sin_cupo':
      return { texto: t('oficinaV2.chat.hito.sinCupo'), tono: 'normal' };
    case 'trasladado':
      return { texto: t('oficinaV2.chat.hito.trasladado'), tono: 'normal' };
    case 'consulta_github':
      return { texto: t('oficinaV2.chat.hito.consultaGithub'), tono: 'normal' };
    case 'consulta_agentes':
      return { texto: t('oficinaV2.chat.hito.consultaAgentes'), tono: 'normal' };
    case 'permiso_respondido':
      return {
        texto: t(
          ({ aprobado: 'oficinaV2.chat.hito.permisoAprobado', hecho: 'oficinaV2.chat.hito.permisoHecho', no_se_pudo: 'oficinaV2.chat.hito.permisoNoSePudo', denegado: 'oficinaV2.chat.hito.permisoDenegado' } as const)[h.resultado],
        ),
        tono: h.resultado === 'no_se_pudo' ? 'error' : 'normal',
      };
    case 'fase':
      return { texto: faseEnCristiano(h.fase), tono: 'normal' };
    case 'contrato_propuesto':
      return { texto: t('oficinaV2.chat.hito.contratoPropuesto'), tono: 'te_necesita' };
    case 'pregunta':
      return { texto: t('oficinaV2.chat.hito.pregunta'), tono: 'te_necesita' };
    case 'permiso':
      return { texto: t('oficinaV2.chat.hito.permiso'), tono: 'te_necesita' };
    case 'informe':
      return { texto: t(h.modo === 'integrar' ? 'oficinaV2.chat.hito.notaEntrega' : 'oficinaV2.chat.hito.informeCierre'), tono: 'normal' };
    case 'pensó':
      return { texto: t('oficinaV2.chat.hito.penso', { segundos: h.segundos }), tono: 'normal' };
    case 'error':
      return { texto: t('oficinaV2.chat.hito.error'), tono: 'error' };
    case 'espera_confirmacion':
      return { texto: t('oficinaV2.chat.hito.esperaConfirmacion'), tono: 'te_necesita' };
  }
}

const FASES = new Set(['orientandose', 'esperando_contrato', 'trabajando', 'verificando', 'bloqueado', 'cerrando', 'terminado']);
export function faseEnCristiano(f: string): string {
  return FASES.has(f) ? t(`oficinaV2.fase.${f as Fase}`) : t('oficinaV2.desconocido');
}

function PildoraHito({ hito }: { hito: Hito }) {
  const { texto, tono } = textoHito(hito);
  return (
    <p className="flex justify-center">
      <Servicio tono={tono}>{texto}</Servicio>
    </p>
  );
}
